import React, { useEffect, useState } from 'react';
import { collection, query, where, orderBy, getDocs, onSnapshot, doc, getDoc } from 'firebase/firestore';
import { db, auth } from '../services/firebase';
import { useAuthState } from 'react-firebase-hooks/auth';
import { CheckCircle, XCircle, Clock, AlertTriangle, Wallet, TrendingUp, Trophy, Target, ChevronRight, Calendar } from 'lucide-react';
import moment from 'moment';
import PredictionHistory from '../components/PredictionHistory';

const UserPredictions = () => {
  const [user] = useAuthState(auth);
  const [predictions, setPredictions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [matches, setMatches] = useState({});
  const [userWallet, setUserWallet] = useState({ walletA: 0, walletB: 0 });
  const [stats, setStats] = useState({
    totalBets: 0,
    totalWinnings: 0,
    correctPredictions: 0,
    pendingAmount: 0
  });
  const [activeTab, setActiveTab] = useState('history');

  useEffect(() => {
    const fetchMatches = async () => {
      try {
        const matchesSnapshot = await getDocs(collection(db, 'matches'));
        const matchesData = {};
        
        matchesSnapshot.docs.forEach(doc => {
          matchesData[doc.id] = { id: doc.id, ...doc.data() };
        });
        
        setMatches(matchesData);
      } catch (error) {
        console.error('Error fetching matches:', error);
      }
    };
    
    fetchMatches();
  }, []);

  // Fetch user wallet
  useEffect(() => {
    const fetchUserWallet = async () => {
      if (!user) return;
      
      try {
        const userDocRef = doc(db, 'users', user.uid);
        const userDoc = await getDoc(userDocRef);
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setUserWallet({
            walletA: userData.walletA || 0,
            walletB: userData.walletB || 0
          });
        }
      } catch (error) {
        console.error('Error fetching user wallet:', error);
      }
    };

    fetchUserWallet();
  }, [user]);

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    try {
      // Set up the predictions query
      const predictionsQuery = query(
        collection(db, 'predictions'),
        where('userId', '==', user.uid),
        orderBy('createdAt', 'desc')
      );
      
      // Subscribe to live updates
      const unsubscribe = onSnapshot(predictionsQuery, (snapshot) => {
        const predictionData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate() || new Date()
        }));
        
        setPredictions(predictionData);
        
        // Calculate stats
        const totalBets = predictionData.length;
        const correctPredictions = predictionData.filter(p => p.status === 'correct').length;
        const totalWinnings = predictionData
          .filter(p => p.status === 'correct')
          .reduce((sum, p) => sum + (p.potentialWinning || p.betAmount * 3), 0);
        const pendingAmount = predictionData
          .filter(p => p.status === 'pending')
          .reduce((sum, p) => sum + (p.betAmount || 0), 0);
        
        setStats({
          totalBets,
          totalWinnings,
          correctPredictions,
          pendingAmount
        });
        
        setLoading(false);
      }, (error) => {
        console.error('Error fetching predictions:', error);
        setLoading(false);
      });
      
      // Return the unsubscribe function for cleanup
      return () => {
        if (typeof unsubscribe === 'function') {
          unsubscribe();
        }
      };
    } catch (error) {
      console.error('Error setting up predictions listener:', error);
      setLoading(false);
      // Return a no-op function if there's an error
      return () => {};
    }
  }, [user]);

  const getPredictionStatusColor = (status) => {
    switch (status) {
      case 'correct':
        return 'bg-green-100 text-green-800 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800/30';
      case 'wrong':
        return 'bg-red-100 text-red-800 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800/30';
      case 'pending':
      default:
        return 'bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-300 dark:border-yellow-800/30';
    }
  };

  const getPredictionStatusIcon = (status) => {
    switch (status) {
      case 'correct':
        return <CheckCircle size={18} className="text-green-600 dark:text-green-400" />;
      case 'wrong':
        return <XCircle size={18} className="text-red-600 dark:text-red-400" />;
      case 'pending':
      default:
        return <Clock size={18} className="text-yellow-600 dark:text-yellow-400" />;
    }
  };

  const formatPredictionType = (type) => {
    switch (type) {
      case 'winner':
        return 'Match Winner';
      case 'first_inning_score':
        return 'First Innings Score';
      case 'over_runs':
        return 'Runs in an Over';
      default:
        return type;
    }
  };

  if (!user) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl p-8 shadow-md text-center">
        <div className="bg-yellow-50 dark:bg-yellow-900/30 p-3 rounded-full inline-flex mb-4">
          <AlertTriangle size={40} className="text-yellow-500" />
        </div>
        <h3 className="text-xl font-bold mb-3 dark:text-white">Sign In Required</h3>
        <p className="text-gray-600 dark:text-gray-300 max-w-md mx-auto">
          Please sign in to view your predictions and track your winnings.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Stats */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl p-6 shadow-md">
        <h2 className="text-2xl font-bold mb-6">Your Predictions</h2>
        
        {/* Wallet Info */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="bg-white/20 p-2 rounded-lg">
                <Wallet size={20} />
              </div>
              <span className="text-sm font-medium">Game Coins</span>
            </div>
            <div className="text-2xl font-bold">{userWallet.walletA || 0}</div>
            <div className="text-xs text-blue-100 mt-1">Use to place predictions</div>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="bg-white/20 p-2 rounded-lg">
                <TrendingUp size={20} />
              </div>
              <span className="text-sm font-medium">Cash Balance</span>
            </div>
            <div className="text-2xl font-bold">{userWallet.walletB || 0} <span className="text-sm opacity-80">= ₹{((userWallet.walletB || 0) * 0.01).toFixed(1)}</span></div>
            <div className="text-xs text-blue-100 mt-1">Available for withdrawal</div>
          </div>
        </div>

        {/* Stats section removed */}
      </div>
      
      {/* Navigation Tabs */}
      <div className="bg-white dark:bg-gray-800 rounded-xl overflow-hidden shadow-md">
        <div className="flex border-b border-gray-200 dark:border-gray-700">
          <button 
            className={`flex-1 py-4 px-6 text-center font-medium text-sm transition-colors ${
              activeTab === 'history' 
                ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400' 
                : 'text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400'
            }`}
            onClick={() => setActiveTab('history')}
          >
            Prediction History
          </button>
          <button 
            className={`flex-1 py-4 px-6 text-center font-medium text-sm transition-colors ${
              activeTab === 'stats' 
                ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400' 
                : 'text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400'
            }`}
            onClick={() => setActiveTab('stats')}
          >
            Detailed Stats
          </button>
        </div>
        
        <div className="p-6">
          {activeTab === 'history' ? (
            <PredictionHistory />
          ) : (
            <div className="text-center py-8">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 dark:bg-blue-900/30 mb-4">
                <TrendingUp size={28} className="text-blue-600 dark:text-blue-400" />
              </div>
              <h3 className="text-xl font-bold mb-2 dark:text-white">Coming Soon</h3>
              <p className="text-gray-600 dark:text-gray-300 max-w-md mx-auto">
                Detailed statistics and analytics about your prediction performance will be available soon.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserPredictions;